import Flex from "common/Boxes/Flex";
import Card from "common/Boxes/Card";
import ScoreInfo from "features/newMarketplace/common/ScoreInfo";
import BacktestDetailsProvider from "@shared/features/bots/BacktestDetailsProvider";
import BacktestingStatistics from "features/bots/BotForm/Backtesting/ExtraDetailsDrawer/BacktestingStatistics";
import React, { useContext } from "react";
import styled, { useTheme } from "styled-components";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { getNewLookTokens, NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import Title from "common/Typography/Title";
import { hexToRGB } from "@shared/utils/misc";
import { GROUP_DRAWER_EXTENSION_WIDTH } from "features/newMarketplace/common/constants";
import { SubGroupDetailsContext } from "@shared/features/newMarketplace/SubGroupDetails.context";
import Disclaimer from "features/newMarketplace/GroupExtraDetails/Disclaimer";

interface DrawerExtensionProps {
  isScoreHover?: boolean;
}

const DrawerExtension = ({ isScoreHover }: DrawerExtensionProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { isNewLook } = useTheme();
  const { groupOverview, getGroupOverviewQuery } = useContext(
    SubGroupDetailsContext
  )!;
  const backtestingRun =
    groupOverview?.timeframe_to_backtesting_run_data?.[180];
  const isLoading = getGroupOverviewQuery?.isLoading;

  return (
    <ExtensionWrapper>
      <ExtensionTitle>
        {isNewLook ? t("backtesting.statistics_title") : "Statistics"}
      </ExtensionTitle>
      <Disclaimer inExtension={true} style={{ marginTop: 16 }} />
      <Flex
        $alignItems="flex-start"
        style={{ columnGap: PADDING, marginTop: PADDING }}
      >
        <ScoreCard $isHover={isScoreHover}>
          <ScoreInfo
            group={groupOverview}
            loading={isLoading}
            detailed={true}
          />
        </ScoreCard>
        <div
          style={{
            width: GROUP_DRAWER_EXTENSION_WIDTH - SCORE_WIDTH - PADDING * 3,
          }}
        >
          <BacktestDetailsProvider backtestingRun={backtestingRun}>
            <BacktestingStatistics
              loading={isLoading}
              isGroupDrawer={true}
              style={{ overflowY: "unset", padding: 0 }}
            />
          </BacktestDetailsProvider>
        </div>
      </Flex>
    </ExtensionWrapper>
  );
};

export default DrawerExtension;

const SCORE_WIDTH = 386;
const PADDING = 20;

const ExtensionWrapper = styled.div`
  background-color: ${(p) =>
    p.theme.isNewLook
      ? getNewLookTokens(p.theme).canvas
      : p.theme.colors.statisticsBgColor};
  padding: ${PADDING}px;
  overflow-y: auto;
  height: 100%;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const ExtensionTitle = styled(Title).attrs({ level: 2 })`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  ${(p) =>
    p.theme.isNewLook &&
    `
    color: ${getNewLookTokens(p.theme).ink};
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
  `}
`;
const ScoreCard = styled(Card)<{ $isHover?: boolean }>`
  padding: 20px;
  width: ${SCORE_WIDTH}px;
  border: 4px solid transparent;
  transition: border-color linear 0.3s;
  ${(p) =>
    p.$isHover &&
    `
    border-color: ${hexToRGB(p.theme.colors.primaryBlue, 0.4)};
  `};
  ${(p) =>
    p.theme.isNewLook &&
    `
    border: ${p.theme.borders.panel};
    border-radius: ${NEW_LOOK_RADIUS.panel}px;
    background: ${getNewLookTokens(p.theme).panel};
    transition: box-shadow 150ms;
    box-shadow: ${
      p.$isHover
        ? `0 0 0 2px ${getNewLookTokens(p.theme).focus}`
        : p.theme.shadows.panel
    };
  `};
`;
